import { Badge, Box, Button, Drawer, ScrollArea, Select, Table, Text } from '@mantine/core';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IOrder } from '../../interfaces/Order';
import { IOrderProduct } from '../../interfaces/Product';
import { useGetStoreAssignableStaff } from '../../hooks/staff/queries';
import { useUpdateOrderLineAssignments } from '../../hooks/orders/mutations';
import useCanAssignOrderLineEmployees from '../../hooks/useCanAssignOrderLineEmployees';
import { showNotification } from '../../utils/Notification/Notification';
import {
  buildInitialAssignedEmployeeMap,
  buildOrderLineAssignmentPayload,
  getAssignedEmployeeId,
  getAssignedEmployeeLabel,
  getEmployeeDisplayName,
  getOrderLineAssignmentKey,
  hasOrderLineAssignmentChanges,
  isOrderLineAssignable,
  LineAssignmentMap,
  OrderLineCollection
} from '../../utils/orderLineAssignments';
import LoaderOverlay from '../LoaderOverlay/LoaderOverlay';

const UNASSIGNED_SELECT_VALUE = '__unassigned__';

type AssignmentLine = {
  key: string;
  line: IOrderProduct;
};

const getOrderLines = (
  order: IOrder | null,
  collection: OrderLineCollection
): AssignmentLine[] => {
  const lines = order?.[collection] || [];
  if (!Array.isArray(lines)) return [];

  return (lines as IOrderProduct[])
    .map((line, index) => ({
      key: getOrderLineAssignmentKey(line, collection, index),
      line
    }))
    .filter(({ line }) => isOrderLineAssignable(line));
};

export default memo(function OrderLineAssignmentModal({
  opened,
  close,
  order,
  orderId,
  onSaved
}: {
  opened: boolean;
  close: () => void;
  order: IOrder | null;
  orderId: string;
  onSaved?: (updatedOrder: IOrder) => void;
}) {
  const { t } = useTranslation(['OrdersPage']);
  const updateAssignments = useUpdateOrderLineAssignments();
  const canAssignEmployees = useCanAssignOrderLineEmployees();
  const [lineAssignments, setLineAssignments] = useState<LineAssignmentMap>({});

  const storeId =
    typeof order?.createdIn === 'string' ? order.createdIn : order?.createdIn?._id || '';
  const { data: staffs = [], isLoading: isLoadingStaffs } = useGetStoreAssignableStaff(
    opened && canAssignEmployees && !!storeId,
    storeId,
    'appendAdmin=true'
  );

  useEffect(() => {
    if (opened) {
      setLineAssignments(buildInitialAssignedEmployeeMap(order));
    }
  }, [opened, order]);

  const lines = useMemo(
    () => [...getOrderLines(order, 'products'), ...getOrderLines(order, 'buyAndGetProducts')],
    [order]
  );

  const employeeOptions = useMemo(() => {
    const options = [
      {
        value: UNASSIGNED_SELECT_VALUE,
        label: t('OrdersPage:productsModal.unassigned')
      },
      ...staffs
        .filter((staff) => !!staff._id)
        .map((staff) => ({
          value: staff._id!,
          label: getEmployeeDisplayName(staff)
        }))
    ];

    const optionValues = new Set(options.map((option) => option.value));
    lines
      .map(({ line }) => ({
        id: getAssignedEmployeeId(line.assignedEmployee),
        label: getAssignedEmployeeLabel(
          line.assignedEmployee,
          staffs,
          t('OrdersPage:productsModal.unassigned')
        )
      }))
      .filter(({ id }) => !!id && !optionValues.has(id))
      .forEach(({ id, label }) => {
        options.push({
          value: id!,
          label
        });
        optionValues.add(id!);
      });

    return options;
  }, [lines, staffs, t]);

  const hasChanges = useMemo(
    () => hasOrderLineAssignmentChanges(order, lineAssignments),
    [order, lineAssignments]
  );

  const handleSave = async () => {
    if (!canAssignEmployees || !order || !orderId || !hasChanges) return;

    try {
      const payload = buildOrderLineAssignmentPayload(order, lineAssignments);
      const updatedOrder = await updateAssignments.mutateAsync({
        orderId,
        updates: payload.updates
      });
      onSaved?.(updatedOrder as IOrder);
      showNotification({
        type: 'Default',
        title: t('OrdersPage:productsModal.assignmentSuccessMessage')
      });
      close();
    } catch {
      // backendErrorsHandler in the mutation owns the error notification.
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={close}
      title={t('OrdersPage:lineAssignmentModal.title')}
      position="right"
      size="lg"
      scrollAreaComponent={ScrollArea.Autosize}>
      <LoaderOverlay visible={updateAssignments.isPending} />
      <Text size="sm" c="dimmed" mb="md">
        {t('OrdersPage:lineAssignmentModal.helperText')}
      </Text>
      {lines.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          {t('OrdersPage:lineAssignmentModal.emptyState')}
        </Text>
      ) : (
        <>
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('OrdersPage:lineStatusModal.product')}</Table.Th>
                <Table.Th>{t('OrdersPage:productsModal.assignedTo')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lines.map(({ key, line }) => {
                return (
                  <Table.Tr key={key}>
                    <Table.Td>
                      (x{line.quantity}) {line.name}
                      {line.isBuyAndGetProduct && (
                        <Badge size="xs" ml="xs" variant="light" color="violet">
                          {t('OrdersPage:lineStatusModal.buyAndGet')}
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Select
                        size="xs"
                        data={employeeOptions}
                        value={lineAssignments[key] || UNASSIGNED_SELECT_VALUE}
                        onChange={(value) =>
                          setLineAssignments((current) => ({
                            ...current,
                            [key]: !value || value === UNASSIGNED_SELECT_VALUE ? null : value
                          }))
                        }
                        searchable
                        allowDeselect={false}
                        disabled={!canAssignEmployees || isLoadingStaffs || updateAssignments.isPending}
                        aria-label={t('OrdersPage:productsModal.assignedTo')}
                      />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          <Box
            pt="md"
            style={{
              position: 'sticky',
              bottom: 0,
              background: 'var(--mantine-color-body)',
              borderTop: '1px solid var(--mantine-color-gray-3)'
            }}>
            <Button
              fullWidth
              onClick={handleSave}
              loading={updateAssignments.isPending}
              disabled={!canAssignEmployees || !hasChanges}>
              {t('OrdersPage:lineAssignmentModal.save')}
            </Button>
          </Box>
        </>
      )}
    </Drawer>
  );
});
